import { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';

import stats from 'services/stat';
import marketplace from 'services/marketplace';

import { ACTIVITY_TYPES } from 'constants/index';
import useTokenContext from 'hooks/useTokenContext';

const initialState = {
  items: [],
  perPage: 25,
  loading: true,
  filters: ['sale'],
  infiniteScrollLoading: false,
  contractAddress: null,
  address: null,
  filteredCollection: null,
  allLoaded: false,
  lastDate: null,
};

const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ITEMS: 'SET_ITEMS',
  ADD_ITEMS: 'ADD_ITEMS',
  SET_PER_PAGE: 'SET_PER_PAGE',
  SET_FILTERS: 'SET_FILTERS',
  SET_INFINITE_SCROLL_LOADING: 'SET_INFINITE_SCROLL_LOADING',
  SET_CONTRACT_ADDRESS: 'SET_CONTRACT_ADDRESS',
  SET_ADDRESS: 'SET_ADDRESS',
  SET_FILTERED_COLLECTION: 'SET_FILTERED_COLLECTION',
  SET_ALL_LOADED: 'SET_ALL_LOADED',
};

const ActivityReducer = (state, action = null) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case ACTIONS.SET_ITEMS:
      return {
        ...state,
        loading: false,
        items: action.payload.data,
        lastDate:
          action.payload.data.length > 0
            ? action.payload.data[action.payload.data.length - 1].date
            : null,
      };
    case ACTIONS.ADD_ITEMS: {
      const newItems = [...state.items, ...action.payload.data];
      return {
        ...state,
        items: newItems,
        lastDate: newItems.length > 0 ? newItems[newItems.length - 1].date : null,
      };
    }
    case ACTIONS.SET_PER_PAGE:
      return {
        ...state,
        perPage: action.payload,
      };
    case ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: action.payload,
        lastDate: null,
      };
    case ACTIONS.SET_INFINITE_SCROLL_LOADING:
      return {
        ...state,
        infiniteScrollLoading: action.payload,
      };
    case ACTIONS.SET_CONTRACT_ADDRESS:
      return {
        ...state,
        contractAddress: action.payload,
        lastDate: null,
      };
    case ACTIONS.SET_ADDRESS:
      return {
        ...state,
        address: action.payload,
        lastDate: null,
      };
    case ACTIONS.SET_FILTERED_COLLECTION:
      return {
        ...state,
        filteredCollection: action.payload,
      };
    case ACTIONS.SET_ALL_LOADED:
      return {
        ...state,
        allLoaded: action.payload,
      };
  }

  throw Error('Unknown action: ' + action.type);
};

export const ActivityContext = createContext(null);

export const ActivityProvider = ({ children, initialFilters }) => {
  // Use custom initial filters if provided, otherwise use defaults
  const customInitialState = initialFilters
    ? { ...initialState, filters: initialFilters }
    : initialState;

  const [state, dispatch] = useReducer(ActivityReducer, customInitialState);
  const { connectedAndAuth } = useTokenContext();

  const setLoading = (value) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: value });
  };

  const parsedFilters = useMemo(() => {
    if (Boolean(state.filters.length)) {
      return Object.fromEntries(
        Object.keys(ACTIVITY_TYPES).map((item) => [item, state.filters.includes(item)]),
      );
    }
    return {};
  }, [state.filters]);

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const response = await stats.getActivities({
        limit: state.perPage,
        ...(state.filteredCollection ? { collection: state.filteredCollection.address } : {}),
        ...parsedFilters,
      });
      if (response.data.length === 0) {
        setAllLoaded(true);
        return;
      }
      setAllLoaded(false);
      setItems(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreActivity = async () => {
    try {
      const date = state.lastDate;
      const skip = state.items.reduce((count, item) => (item.date === date ? count + 1 : count), 0);
      const response = await stats.getActivities({
        limit: state.perPage,
        skip,
        date,
        ...(state.filteredCollection ? { collection: state.filteredCollection.address } : {}),
        ...parsedFilters,
      });
      if (response.data.length === 0) {
        setAllLoaded(true);
        return;
      }
      setAllLoaded(false);
      addItems(response);
    } catch (error) {
      console.error(error);
    } finally {
      setInfiniteScrollLoading(false);
    }
  };

  const fetchUserActivity = async () => {
    setLoading(true);
    try {
      const response = await stats.getUserActivities({
        address: state.address,
        skip: 0,
        limit: state.perPage,
        ...(state.filteredCollection ? { collection: state.filteredCollection.address } : {}),
        ...parsedFilters,
      });
      setAllLoaded(response.data.length === 0);
      setItems(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreUserActivity = async () => {
    try {
      const date = state.lastDate;
      const skip = state.items.reduce((count, item) => (item.date === date ? count + 1 : count), 0);
      const response = await stats.getUserActivities({
        address: state.address,
        skip,
        date,
        limit: state.perPage,
        ...(state.filteredCollection ? { collection: state.filteredCollection.address } : {}),
        ...parsedFilters,
      });
      if (!response || response.data.length === 0) {
        setAllLoaded(true);
        return;
      }
      setAllLoaded(false);
      addItems(response);
    } catch (error) {
      console.error(error);
    } finally {
      setInfiniteScrollLoading(false);
    }
  };

  const fetchContractActivity = useCallback(async () => {
    setLoading(true);
    try {
      const response = await marketplace.getCollectionTradingHistory({
        contractAddress: state.contractAddress,
        limit: state.perPage,
        ...parsedFilters,
      });
      setAllLoaded(response.data.length === 0);
      setItems(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [state.contractAddress, state.perPage, parsedFilters]);

  const fetchMoreContractActivity = useCallback(async () => {
    try {
      const date = state.lastDate;
      const skip = state.items.reduce((count, item) => (item.date === date ? count + 1 : count), 0);
      const response = await marketplace.getCollectionTradingHistory({
        contractAddress: state.contractAddress,
        skip,
        date,
        limit: state.perPage,
        ...parsedFilters,
      });
      if (response.data.length === 0) {
        setAllLoaded(true);
        return;
      }
      setAllLoaded(false);
      addItems(response);
    } catch (error) {
      console.error(error);
    } finally {
      setInfiniteScrollLoading(false);
    }
  }, [state.contractAddress, state.items, state.perPage, parsedFilters, state.lastDate]);

  const setItems = (items) => {
    dispatch({ type: ACTIONS.SET_ITEMS, payload: items });
  };

  const addItems = (items) => {
    dispatch({ type: ACTIONS.ADD_ITEMS, payload: items });
  };

  const setAllLoaded = (value) => {
    dispatch({ type: ACTIONS.SET_ALL_LOADED, payload: value });
  };

  const setPerPage = (value) => {
    dispatch({ type: ACTIONS.SET_PER_PAGE, payload: value });
  };

  const setFilters = (value) => {
    dispatch({ type: ACTIONS.SET_FILTERS, payload: value ?? [] });
  };

  const setInfiniteScrollLoading = (value) => {
    dispatch({ type: ACTIONS.SET_INFINITE_SCROLL_LOADING, payload: value });
  };

  const setContractAddress = (value) => {
    dispatch({ type: ACTIONS.SET_CONTRACT_ADDRESS, payload: value });
  };

  const setFilteredCollection = (collection) => {
    dispatch({ type: ACTIONS.SET_FILTERED_COLLECTION, payload: collection });
  };

  const setAddress = (address) => {
    dispatch({ type: ACTIONS.SET_ADDRESS, payload: address });
  };

  useEffect(() => {
    if (state.contractAddress) {
      fetchContractActivity();
    }
  }, [state.contractAddress, parsedFilters]);

  useEffect(() => {
    if (state.address) {
      fetchUserActivity();
    }
  }, [state.address, state.filteredCollection, parsedFilters]);

  useEffect(() => {
    if (connectedAndAuth === false) {
      setLoading(false);
    }
  }, [connectedAndAuth]);

  return (
    <ActivityContext.Provider
      value={{
        state,
        dispatch,
        setLoading,
        fetchMoreActivity,
        setPerPage,
        setFilters,
        parsedFilters,
        fetchUserActivity,
        fetchActivity,
        setInfiniteScrollLoading,
        fetchMoreUserActivity,
        setContractAddress,
        fetchContractActivity,
        fetchMoreContractActivity,
        setFilteredCollection,
        setAddress,
        setAllLoaded,
      }}
    >
      {children}
    </ActivityContext.Provider>
  );
};
